import type { RemediationItemCounts } from '@libs/types';

/**
 * Documents the AI settled that are not failed, not still in flight, and not
 * "nothing to change" — in other words, the ones that actually produced a
 * result a human accepted or rejected.
 *
 * Derived rather than reported, because `RemediationItemCounts` is deliberately
 * a partition of outcomes the *backend* cares about (what is outstanding, what
 * broke) and this is the one the user asks for: "how many documents did this
 * remediation actually improve".
 *
 * Clamped at zero: the counts come from a live tally and an item settling
 * between two reads could otherwise make the subtraction go negative for a
 * frame.
 */
export function appliedCount(counts: RemediationItemCounts): number {
  return Math.max(
    0,
    counts.total - counts.reviewable - counts.noChanges - counts.failed - counts.inFlight,
  );
}

/** Whether a run has anything a person still has to decide. */
export function hasOutstandingReview(counts: RemediationItemCounts): boolean {
  return counts.reviewable > 0;
}

/**
 * Totals across every run on one assessment's lineage.
 *
 * Summed rather than taken from the newest run: an assessment remediated over
 * three passes has improved the sum of what those passes improved, and reporting
 * only the last one would erase the earlier work from its own history.
 */
export function totalsAcrossRuns(runs: readonly { itemCounts: RemediationItemCounts }[]): {
  runs: number;
  applied: number;
  failed: number;
  documents: number;
} {
  return runs.reduce(
    (acc, run) => ({
      runs: acc.runs + 1,
      applied: acc.applied + appliedCount(run.itemCounts),
      failed: acc.failed + run.itemCounts.failed,
      documents: acc.documents + run.itemCounts.total,
    }),
    { runs: 0, applied: 0, failed: 0, documents: 0 },
  );
}
